import React, { useMemo, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { Add, Visibility } from "@mui/icons-material";
import InfiniteScrollTable from "app/components/Tables/InfiniteScrollTable";
import moment from "moment";
import { toast } from "react-toastify";
import { useQuery } from "react-query";
import CommonFilterDialog from "app/components/common/Dialogs/CommonFilterDialog";
import { getTicket } from "app/services/schoolReportingServices/zohoDesk.service";
import { getModuleWithScreenNameList } from "app/services/schoolReportingServices/access.service";
import CreateTicket from "./components/CreateTicket/CreateTicket";
import { useNavigate } from "react-router-dom";
import appRoutes from "app/config/routes";

const HelpAndSupport = () => {
  const navigate = useNavigate();

  // Fetch modules and screen names using useQuery
  const {
    data: moduleScreenData,
    isLoading: isModuleLoading,
    error: moduleError,
  } = useQuery({
    queryKey: ["moduleWithScreenNameList"],
    queryFn: async () => {
      const res = await getModuleWithScreenNameList();
      return res;
    },
    // Handles both { moduleList: [...] } and { data: { moduleList: [...] } }
    select: (data) => data?.moduleList || data?.data?.moduleList || [],
  });

  // Single state object for all filter and dialog state
  const [state, setState] = useState({
    openFilterDialog: false,
    openCreateDialog: false,
    priority: "",
    status: "",
    module: "",
    enabled: false,
  });

  // Table columns
  const columns = useMemo(
    () => [
      {
        header: "Sr No",
        Cell: ({ cell }) =>
          Number(cell.row.id) + 1 < 10
            ? `0${Number(cell.row.id) + 1}`
            : Number(cell.row.id) + 1,
        maxSize: 30,
      },
      {
        header: "Ticket No.",
        accessorKey: "ticketNumber",
        maxSize: 40,
      },
      {
        header: "Module",
        accessorKey: "module",
        maxSize: 50,
      },
      {
        header: "Screen",
        accessorKey: "screenName",
        maxSize: 50,
      },
      {
        header: "Priority",
        accessorKey: "priority",
        Cell: ({ cell }) => (
          <Typography
            sx={{
              color:
                cell.getValue() === "High"
                  ? "red"
                  : cell.getValue() === "Medium"
                  ? "orange"
                  : "green",
            }}
          >
            {cell.getValue()}
          </Typography>
        ),
        maxSize: 30,
      },
      {
        header: "Status",
        accessorKey: "status",
        maxSize: 30,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          let color = "black";
          if (value === "Closed") color = "green";
          else if (value === "Excalated") color = "#001F54"; // Darkest Blue
          else if (value === "On Hold") color = "orange";
          else if (value === "Open") color = "black";
          return <Typography sx={{ color }}>{value}</Typography>;
        },
      },
      {
        header: "Created On",
        accessorKey: "createdOn",
        Cell: ({ cell }) => moment(cell.getValue()).format("DD MMM, YYYY"),
        maxSize: 30,
      },
      {
        header: "Created By",
        accessorKey: "createdBy.personName",
        maxSize: 40,
      },
      {
        header: "Last Updated",
        accessorKey: "updatedOn",
        Cell: ({ cell }) => moment(cell.getValue()).format("DD MMM, YYYY"),
        maxSize: 30,
      },
      {
        header: "Action",
        maxSize: 40,
        Cell: ({ row }) => (
          <Stack direction="row" gap={1.5} alignItems="center">
            <Box
              onClick={() =>
                navigate(
                  `/${appRoutes.ticketDetails.replace(
                    ":ticketId",
                    row.original.ticketId
                  )}`
                )
              }
              sx={{
                width: "30px",
                height: "30px",
                alignItems: "center",
                display: "flex",
                justifyContent: "center",
                borderRadius: "5px",
                cursor: "pointer",
                backgroundColor: "#EBF0F4",
              }}
            >
              <Visibility sx={{ color: "#0B4977" }} />
            </Box>
          </Stack>
        ),
      },
    ],
    [navigate]
  );

  // Build apiParams from state
  const apiParams = useMemo(
    () => ({
      ...(state.priority && { priority: state.priority }),
      ...(state.status && { status: state.status }),
      ...(state.module && { module: state.module }),
    }),
    [state.priority, state.status, state.module]
  );

  // Handle filter input changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setState((prev) => ({ ...prev, [name]: value }));
  };

  // Handle Save & View in the dialog
  const handleSaveAndView = () => {
    setState((prev) => ({
      ...prev,
      openFilterDialog: false,
      enabled: !prev.enabled, // Toggle enabled to force refetch
    }));
  };

  return (
    <>
      <Box sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="flex-end" gap={2} mb={2}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() =>
              setState((prev) => ({ ...prev, openCreateDialog: true }))
            }
          >
            Create Ticket
          </Button>
          <Button
            variant="contained"
            onClick={() =>
              setState((prev) => ({ ...prev, openFilterDialog: true }))
            }
          >
            Open Filters
          </Button>
        </Stack>
        <InfiniteScrollTable
          columns={columns}
          apiUrl={getTicket}
          apiParams={apiParams}
          maxHeight="65vh"
          maxWidth="100vw"
          enabled={state.enabled}
          setEnabled={(val) => setState((prev) => ({ ...prev, enabled: val }))}
          setState={() => {}} // No-op, not used
          onError={(error) =>
            toast.error(
              error?.response?.data?.message || "Failed to fetch tickets"
            )
          }
        />
      </Box>

      {/* Create Ticket Dialog */}
      <CreateTicket
        open={state.openCreateDialog}
        onClose={() =>
          setState((prev) => ({ ...prev, openCreateDialog: false }))
        }
        onTicketCreated={() => {
          // Only refetch when a ticket is actually created successfully
          setState((prev) => ({ ...prev, enabled: !prev.enabled }));
        }}
      />

      <CommonFilterDialog
        open={state.openFilterDialog}
        onClose={() =>
          setState((prev) => ({ ...prev, openFilterDialog: false }))
        }
        title="Ticket Filters"
        handleSaveAndView={handleSaveAndView}
      >
        <Box sx={{ p: 2, minWidth: "400px" }}>
          <Stack spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                name="priority"
                value={state.priority}
                onChange={handleFilterChange}
                label="Priority"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="High">High</MenuItem>
                <MenuItem value="Medium">Medium</MenuItem>
                <MenuItem value="Low">Low</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={state.status}
                onChange={handleFilterChange}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Open">Open</MenuItem>
                <MenuItem value="In Progress">In Progress</MenuItem>
                <MenuItem value="Resolved">Resolved</MenuItem>
                <MenuItem value="Closed">Closed</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Module</InputLabel>
              <Select
                name="module"
                value={state.module}
                onChange={handleFilterChange}
                label="Module"
                disabled={isModuleLoading}
              >
                <MenuItem value="">
                  {isModuleLoading ? "Loading..." : "All"}
                </MenuItem>
                {!isModuleLoading && (moduleScreenData || []).length === 0 && (
                  <MenuItem value="" disabled>
                    No modules found
                  </MenuItem>
                )}
                {(moduleScreenData || []).map((item, index) => (
                  <MenuItem key={index} value={item.module}>
                    {item.module}
                  </MenuItem>
                ))}
              </Select>
              {moduleError && (
                <Typography color="error" variant="caption">
                  Failed to load modules
                </Typography>
              )}
            </FormControl>
          </Stack>
        </Box>
      </CommonFilterDialog>
    </>
  );
};

export default HelpAndSupport;
